import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const themes = {
  light: {
    backgroundColor: 'bg-white',
    textColor: 'text-gray-800',
    borderColor: 'border-gray-300',
    buttonColor: 'bg-purple-600',
    buttonHoverColor: 'hover:bg-purple-700',
    shadow: 'shadow-md'
  },
  dark: {
    backgroundColor: 'bg-gray-800',
    textColor: 'text-white',
    borderColor: 'border-gray-600',
    buttonColor: 'bg-purple-800',
    buttonHoverColor: 'hover:bg-purple-900',
    shadow: 'shadow-none'
  }
};

const AspectRatioCalculator = () => {
  const [dimensions, setDimensions] = useState({
    width: '',
    height: ''
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [theme, setTheme] = useState('light');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDimensions(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateAspectRatio = () => {
    const { width, height } = dimensions;

    if (!width || !height) {
      setError('Please fill in both width and height fields');
      return;
    }

    try {
      const widthNum = parseFloat(width);
      const heightNum = parseFloat(height);

      if (isNaN(widthNum) || isNaN(heightNum)) {
        setError('Please enter valid numbers for width and height');
        return;
      }

      if (widthNum <= 0 || heightNum <= 0) {
        setError('Width and height must be greater than zero');
        return;
      }

      const aspectRatio = widthNum / heightNum;
      const targetRatio = 16 / 9;

      let suggestion = '';
      if (aspectRatio === targetRatio) {
        suggestion = 'Your dimensions match the 16:9 aspect ratio perfectly!';
      } else if (aspectRatio > targetRatio) {
        suggestion = `Your dimensions are wider than the 16:9 aspect ratio. Consider reducing the width or increasing the height.`;
      } else {
        suggestion = `Your dimensions are taller than the 16:9 aspect ratio. Consider increasing the width or reducing the height.`;
      }

      setResult({
        width: widthNum,
        height: heightNum,
        aspectRatio: aspectRatio.toFixed(2),
        suggestion
      });

      setChartData({
        labels: ['16:9 Ratio', 'Your Ratio'],
        datasets: [
          {
            label: 'Aspect Ratio',
            data: [targetRatio, aspectRatio],
            borderColor: ['rgba(75,192,192,1)', 'rgba(153,102,255,1)'],
            borderWidth: 1
          }
        ]
      });

    } catch (error) {
      setError('An error occurred while calculating the aspect ratio');
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const currentTheme = themes[theme];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className={`min-h-screen ${currentTheme.backgroundColor} ${currentTheme.textColor}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>16:9 Aspect Ratio Calculator</title>
          <meta name="description" content="Calculate and optimize the 16:9 aspect ratio for your images or screens." />
          <meta name="keywords" content="16:9 aspect ratio calculator, image dimensions, screen dimensions, aspect ratio optimization" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8 max-w-2xl mx-auto">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-purple-600 mr-4">🔍</div>
            <h1 className="text-3xl font-bold text-gray-800">16:9 Aspect Ratio Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The 16:9 Aspect Ratio Calculator helps you determine if your dimensions match the 16:9 aspect ratio and provides suggestions for adjustments.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Width (pixels):</label>
              <input
                type="number"
                name="width"
                value={dimensions.width}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border ${currentTheme.borderColor} rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                placeholder="Enter width"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Height (pixels):</label>
              <input
                type="number"
                name="height"
                value={dimensions.height}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border ${currentTheme.borderColor} rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                placeholder="Enter height"
              />
            </div>
          </div>

          <button
            onClick={calculateAspectRatio}
            className={`w-full py-3 px-4 ${currentTheme.buttonColor} text-white font-medium rounded-lg ${currentTheme.buttonHoverColor} focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors duration-200`}
          >
            Calculate Aspect Ratio
          </button>

          <button
            onClick={toggleTheme}
            className="mt-4 py-2 px-4 bg-gray-200 text-gray-800 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Toggle Theme
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Aspect Ratio Results</h2>
              
              <div className={`bg-gray-50 p-6 rounded-lg ${currentTheme.shadow}`}>
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculated Aspect Ratio</h3>
                <p className="text-xl font-bold">{result.aspectRatio}</p>
              </div>

              <div className={`bg-gray-50 p-6 rounded-lg mt-6 ${currentTheme.shadow}`}>
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Suggestions</h3>
                <p className="text-gray-600">{result.suggestion}</p>
              </div>

              {chartData && (
                <div className={`bg-white p-6 rounded-lg shadow-md mt-8 ${currentTheme.shadow}`}>
                  <Line
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Aspect Ratio'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Ratios'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Aspect Ratio Comparison'
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* How to Use Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Aspect Ratio Calculator <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Aspect Ratio Calculator effectively:</p>
            
            <div className="step-section mt-4">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Enter Dimensions</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Input the width of your image or screen in pixels.</li>
                <li>Input the height of your image or screen in pixels.</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Calculate</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Click the "Calculate Aspect Ratio" button to get the results.</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Review Results</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Check the calculated aspect ratio.</li>
                <li>Review the suggestions for adjustments if needed.</li>
                <li>Visualize the comparison with the 16:9 ratio using the graph.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ <span className="text-blue-600">❓</span></h2>
          <div className="text-gray-600">
            <div className="faq-item mb-4">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">What is the 16:9 aspect ratio?</h3>
              <p>The 16:9 aspect ratio is a standard format used in widescreen displays and video content. It means that for every 16 units of width, there are 9 units of height.</p>
            </div>

            <div className="faq-item mb-4">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Why is the 16:9 aspect ratio important?</h3>
              <p>The 16:9 aspect ratio is important because it is widely used in modern displays and video content. Maintaining this ratio ensures that your images or videos look correct and are not distorted.</p>
            </div>

            <div className="faq-item mb-4">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">How can I adjust my dimensions to match the 16:9 aspect ratio?</h3>
              <p>If your dimensions do not match the 16:9 aspect ratio, you can adjust them by either changing the width or height. The calculator provides suggestions on how to do this.</p>
            </div>
          </div>
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📈</span></h2>
          <div className="text-gray-600">
            <p>Let's say you have an image with a width of 1920 pixels and a height of 1080 pixels.</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Enter 1920 in the width field.</li>
              <li>Enter 1080 in the height field.</li>
              <li>Click the "Calculate Aspect Ratio" button.</li>
              <li>The calculator will show that the aspect ratio is 1.78, which matches the 16:9 ratio (16/9 = 1.78).</li>
              <li>The graph will visualize this comparison.</li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default  AspectRatioCalculator ;